/**
 * PostHog Analytics Service - Client PWA
 * 
 * Handles all analytics tracking for customer-facing app
 * Tracks user behavior, conversions, and errors
 */

import posthog from 'posthog-js';

const isEnabled = !!import.meta.env.VITE_POSTHOG_API_KEY;
const isSessionRecordingEnabled = import.meta.env.VITE_ENABLE_POSTHOG_SESSION_RECORDING === 'true';
const isAutocaptureEnabled = import.meta.env.VITE_ENABLE_POSTHOG_AUTOCAPTURE === 'true';
let hasRegisteredGlobalHandlers = false;

// Initialize PostHog
const initializeAnalytics = () => {
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY;
  const host = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.posthog.com';

  if (!apiKey) {
    console.warn('[Analytics] PostHog API key not found. Analytics disabled.');
    return;
  }

  try {
    posthog.init(apiKey, {
      api_host: host,
      ui_host: 'https://eu.posthog.com', // EU region UI
      capture_pageview: true,
      capture_pageleave: false,
      autocapture: isAutocaptureEnabled,
      disable_session_recording: !isSessionRecordingEnabled,
      session_recording: isSessionRecordingEnabled
        ? {
            maskAllInputs: true, // Mask sensitive inputs
            maskTextSelector: '[data-sensitive]', // Mask elements with this attribute
          }
        : undefined,
      loaded: (posthog) => {
        // isInitialized = true;
        console.log('[Analytics] PostHog initialized successfully');
        console.log('PostHog initialized:', posthog.__loaded);
      },
      persistence: 'localStorage', // Use localStorage instead of cookies
      cross_subdomain_cookie: false, // Disable cross-subdomain cookies
    });
  } catch (error) {
    console.error('[Analytics] PostHog initialization failed:', error);
  }
};

// Identify user (device user)
export const identifyUser = (deviceUserId: string, properties?: Record<string, any>) => {
  if (!isEnabled) return;

  posthog.identify(deviceUserId, {
    role: 'customer',
    is_guest: true,
    ...properties,
  });
};

// Track page view (manual, if needed)
export const trackPageView = (pageName: string, properties?: Record<string, any>) => {
  if (!isEnabled) return;

  posthog.capture('$pageview', {
    page_name: pageName,
    ...properties,
  });
};

// Product Events
export const trackProductViewed = (product: {
  id: string;
  name: string;
  price: number;
  category?: string;
}) => {
  if (!isEnabled) return;

  posthog.capture('product_viewed', {
    product_id: product.id,
    product_name: product.name,
    product_price: product.price,
    product_category: product.category,
  });
};

export const trackSearchPerformed = (query: string, resultsCount: number) => {
  if (!isEnabled) return;

  posthog.capture('search_performed', {
    search_query: query,
    results_count: resultsCount,
  });
};

// Cart Events
export const trackAddToCart = (product: {
  id: string;
  name: string;
  price: number;
  quantity: number;
}) => {
  if (!isEnabled) return;

  posthog.capture('add_to_cart', {
    product_id: product.id,
    product_name: product.name,
    product_price: product.price,
    quantity: product.quantity,
    cart_value: product.price * product.quantity,
  });
};

export const trackRemoveFromCart = (product: {
  id: string;
  name: string;
  price: number;
}) => {
  if (!isEnabled) return;

  posthog.capture('remove_from_cart', {
    product_id: product.id,
    product_name: product.name,
    product_price: product.price,
  });
};

export const trackCartViewed = (cartValue: number, itemCount: number) => {
  if (!isEnabled) return;

  posthog.capture('cart_viewed', {
    cart_value: cartValue,
    item_count: itemCount,
  });
};

// Checkout Events
export const trackCheckoutStarted = (cartValue: number, itemCount: number) => {
  if (!isEnabled) return;

  posthog.capture('checkout_started', {
    cart_value: cartValue,
    item_count: itemCount,
  });
};

export const trackDeliveryInfoEntered = () => {
  if (!isEnabled) return;

  posthog.capture('delivery_info_entered');
};

export const trackPaymentMethodSelected = (method: string) => {
  if (!isEnabled) return;

  posthog.capture('payment_method_selected', {
    payment_method: method,
  });
};

export const trackPaymentProofUploaded = (orderId: string) => {
  if (!isEnabled) return;

  posthog.capture('payment_proof_uploaded', {
    order_id: orderId,
  });
};

export const trackOrderCompleted = (order: {
  id: string;
  total: number;
  itemCount: number;
  paymentMethod: string;
}) => {
  if (!isEnabled) return;

  posthog.capture('order_completed', {
    order_id: order.id,
    order_value: order.total,
    item_count: order.itemCount,
    payment_method: order.paymentMethod,
  });
};

// Order Tracking
export const trackOrderTrackingViewed = (orderId: string) => {
  if (!isEnabled) return;

  posthog.capture('order_tracking_viewed', {
    order_id: orderId,
  });
};

// --- STRUCTURED LIFECYCLE EVENTS ---

export const trackCartEvent = (event: {
  action: 'add_to_cart' | 'remove_from_cart' | 'quantity_update' | 'checkout';
  status: 'started' | 'succeeded' | 'failed' | 'abandoned';
  productId?: string;
  cartValue?: number;
  metadata?: Record<string, any>;
  errorMessage?: string;
}) => {
  if (!isEnabled) return;
  posthog.capture(`cart_${event.action}_${event.status}`, {
    product_id: event.productId,
    cart_value: event.cartValue,
    error_message: event.errorMessage,
    ...event.metadata,
  });
};

export const trackOrderEvent = (event: {
  action: 'placement' | 'fetch';
  status: 'started' | 'succeeded' | 'failed';
  orderId?: string;
  metadata?: Record<string, any>;
  errorMessage?: string;
}) => {
  if (!isEnabled) return;
  posthog.capture(`order_${event.action}_${event.status}`, {
    order_id: event.orderId,
    error_message: event.errorMessage,
    ...event.metadata,
  });
};

export const trackAuthEvent = (event: {
  action: 'login' | 'logout' | 'token_refresh' | 'session_expiration';
  status: 'started' | 'succeeded' | 'failed';
  metadata?: Record<string, any>;
  errorMessage?: string;
}) => {
  if (!isEnabled) return;
  posthog.capture(`auth_${event.action}_${event.status}`, {
    error_message: event.errorMessage,
    ...event.metadata,
  });
};

export const trackSilentFailure = (event: {
  context: string;
  expectedState: string;
  actualState: string;
  metadata?: Record<string, any>;
}) => {
  if (!isEnabled) return;
  posthog.capture('silent_failure_detected', {
    failure_context: event.context,
    expected_state: event.expectedState,
    actual_state: event.actualState,
    ...event.metadata,
  });
};

// Error Tracking
export const trackError = (error: {
  message: string;
  source?: string;
  lineno?: number;
  colno?: number;
  stack?: string;
  context?: string;
}) => {
  if (!isEnabled) return;

  posthog.capture('error_frontend', {
    error_message: error.message,
    error_source: error.source,
    error_line: error.lineno,
    error_column: error.colno,
    error_stack: error.stack,
    error_context: error.context,
  });
};

export const trackApiError = (error: {
  endpoint: string;
  method: string;
  statusCode: number;
  message: string;
}) => {
  if (!isEnabled) return;

  posthog.capture('error_api', {
    api_endpoint: error.endpoint,
    api_method: error.method,
    api_status_code: error.statusCode,
    error_message: error.message,
  });
};

export const trackPaymentFailed = (orderId: string, reason: string) => {
  if (!isEnabled) return;

  posthog.capture('payment_failed', {
    order_id: orderId,
    failure_reason: reason,
  });
};

// Setup global error handler
export const setupErrorTracking = () => {
  // Initialize PostHog first
  initializeAnalytics();

  if (hasRegisteredGlobalHandlers || typeof window === 'undefined') {
    return;
  }

  hasRegisteredGlobalHandlers = true;

  // Then setup error handlers
  window.addEventListener('error', (event) => {
    trackError({
      message: event.message,
      source: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
      context: 'global_error_handler',
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    trackError({
      message: event.reason?.message || 'Unhandled Promise Rejection',
      stack: event.reason?.stack,
      context: 'unhandled_rejection',
    });
  });
};

// Reset user (on logout or clear session)
export const resetUser = () => {
  if (!isEnabled) return;

  posthog.reset();
};

export default posthog;
